import type { 
  NavItem, 
  BreadcrumbItem, 
  NavigationSectionConfig,
  TopNavItem 
} from './types';

/**
 * Get the active top navigation ID based on current path
 */
export function getActiveTopNavId(
  pathname: string,
  allSections: NavigationSectionConfig[]
): string {
  // Find which section contains this path
  for (const section of allSections) {
    const matchingItem = section.items.find(item => 
      pathname === item.path || pathname.startsWith(item.path + '/')
    );
    if (matchingItem) {
      return section.topNavId;
    }
  }
  
  // Default to dashboards
  return 'dashboards';
}

/**
 * Get sidebar sections filtered by active top navigation
 */
export function getSidebarSections(
  activeTopNavId: string,
  allSections: NavigationSectionConfig[],
  hasPermission: (permission: string) => boolean
): NavigationSectionConfig[] {
  // Filter sections by active top navigation and permissions
  const filteredSections = allSections
    .filter(section => section.topNavId === activeTopNavId)
    .map(section => ({
      ...section,
      items: section.items.filter(item =>
        item.permissions.length === 0 || item.permissions.some(p => hasPermission(p))
      )
    }))
    .filter(section => section.items.length > 0);
  
  return filteredSections;
}

/**
 * Transform navigation configuration to ui-kit NavItem[] format for sidebar
 * Filtered by active top navigation and user permissions
 * Returns items with path for onClick handler (not href to avoid page reload)
 */
export function transformToNavItems(
  activeTopNavId: string,
  currentPath: string,
  allSections: NavigationSectionConfig[],
  hasPermission: (permission: string) => boolean
): NavItem[] {
  const sections = getSidebarSections(activeTopNavId, allSections, hasPermission);
  const flatItems: NavItem[] = [];
  
  sections.forEach(section => {
    // Add section header as a group (if there are multiple sections under this top nav)
    if (sections.length > 1) {
      flatItems.push({
        id: `${section.id}-header`,
        label: section.label,
        isGroup: true,
      });
    }
    
    // Add all items in this section
    // Store path in a data attribute that AppLayout can use for onClick
    section.items.forEach(item => {
      flatItems.push({
        id: item.id,
        label: item.label,
        icon: item.icon,
        isActive: currentPath === item.path || currentPath.startsWith(item.path + '/'),
        // Store path for onClick handler in AppLayout
        href: item.path, // Used by AppLayout onClick handler
        disabled: item.disabled, // Mark as not implemented
      });
    });
  });

  return flatItems;
}

/**
 * Generate breadcrumbs based on current path and top navigation structure
 */
export function generateBreadcrumbs(
  pathname: string,
  topNavConfig: TopNavItem[],
  allSections: NavigationSectionConfig[]
): BreadcrumbItem[] {
  // Get the active top navigation for this path
  const activeTopNavId = getActiveTopNavId(pathname, allSections);
  const topNav = topNavConfig.find(nav => nav.id === activeTopNavId);
  
  // Dashboard routes - special case
  if (pathname === '/dashboard') {
    return [
      { label: 'Dashboards', href: '/dashboard' },
      { label: 'Overview', isActive: true }
    ];
  }
  
  // Analytics Dashboard
  if (pathname === '/insurance/analytics') {
    return [
      { label: 'Dashboards', href: '/dashboard' },
      { label: 'Analytics Dashboard (Financial)', isActive: true }
    ];
  }
  
  // User Management routes
  if (pathname === '/admin/users') {
    return [
      { label: topNav?.label || 'Users & Organisations', href: topNav?.href },
      { label: 'Users', isActive: true }
    ];
  }
  
  if (pathname === '/admin/users/create') {
    return [
      { label: topNav?.label || 'Users & Organisations', href: topNav?.href },
      { label: 'Users', href: '/admin/users' },
      { label: 'Create User', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/users/') && pathname.endsWith('/edit')) {
    return [
      { label: topNav?.label || 'Users & Organisations', href: topNav?.href },
      { label: 'Users', href: '/admin/users' },
      { label: 'Edit User', isActive: true }
    ];
  }
  
  // Farmer Management routes
  if (pathname === '/admin/farmers') {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Farmers', isActive: true }
    ];
  }
  
  if (pathname === '/admin/farmers/create') {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Farmers', href: '/admin/farmers' },
      { label: 'Create Farmer', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/farmers/') && pathname.endsWith('/edit')) {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Farmers', href: '/admin/farmers' },
      { label: 'Edit Farmer', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/farmers/') && !pathname.endsWith('/edit')) {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Farmers', href: '/admin/farmers' },
      { label: 'Farmer Details', isActive: true }
    ];
  }
  
  // Shapefile Management routes
  if (pathname === '/admin/shapefiles') {
    return [
      { label: topNav?.label || 'Products & Templates', href: topNav?.href },
      { label: 'Shapefiles', isActive: true }
    ];
  }
  
  // Job Management routes
  if (pathname === '/admin/jobs') {
    return [
      { label: topNav?.label || 'System Administration', href: topNav?.href },
      { label: 'Job Management', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/jobs/') && pathname !== '/admin/jobs') {
    return [
      { label: topNav?.label || 'System Administration', href: topNav?.href },
      { label: 'Job Management', href: '/admin/jobs' },
      { label: 'Job Details', isActive: true }
    ];
  }
  
  // CHIRPS Files Management routes
  if (pathname === '/admin/files') {
    return [
      { label: topNav?.label || 'Products & Templates', href: topNav?.href },
      { label: 'CHIRPS Files', isActive: true }
    ];
  }
  
  // Organization Management routes
  if (pathname === '/admin/organizations') {
    return [
      { label: topNav?.label || 'Users & Organisations', href: topNav?.href },
      { label: 'Organisations', isActive: true }
    ];
  }
  
  if (pathname === '/admin/organizations/create') {
    return [
      { label: topNav?.label || 'Users & Organisations', href: topNav?.href },
      { label: 'Organisations', href: '/admin/organizations' },
      { label: 'Create Organisation', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/organizations/') && pathname.endsWith('/edit')) {
    return [
      { label: topNav?.label || 'Users & Organisations', href: topNav?.href },
      { label: 'Organisations', href: '/admin/organizations' },
      { label: 'Edit Organisation', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/organizations/') && !pathname.endsWith('/edit') && !pathname.endsWith('/create')) {
    return [
      { label: topNav?.label || 'Users & Organisations', href: topNav?.href },
      { label: 'Organisations', href: '/admin/organizations' },
      { label: 'Organisation Details', isActive: true }
    ];
  }

  // Administrative Units Management routes
  if (pathname === '/admin/administrative-units') {
    return [
      { label: topNav?.label || 'Products & Templates', href: topNav?.href },
      { label: 'Administrative Units', isActive: true }
    ];
  }
  
  if (pathname === '/admin/administrative-units/create') {
    return [
      { label: topNav?.label || 'Products & Templates', href: topNav?.href },
      { label: 'Administrative Units', href: '/admin/administrative-units' },
      { label: 'Create Administrative Unit', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/administrative-units/') && pathname.endsWith('/edit')) {
    return [
      { label: topNav?.label || 'Products & Templates', href: topNav?.href },
      { label: 'Administrative Units', href: '/admin/administrative-units' },
      { label: 'Edit Administrative Unit', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/administrative-units/') && !pathname.endsWith('/edit') && !pathname.endsWith('/create')) {
    return [
      { label: topNav?.label || 'Products & Templates', href: topNav?.href },
      { label: 'Administrative Units', href: '/admin/administrative-units' },
      { label: 'Administrative Unit Details', isActive: true }
    ];
  }

  // Quote Management routes
  if (pathname === '/admin/quotes') {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Quotes/Policies', isActive: true }
    ];
  }
  
  if (pathname === '/admin/quotes/create') {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Quotes/Policies', href: '/admin/quotes' },
      { label: 'Create Quote', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/quotes/') && pathname.endsWith('/edit')) {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Quotes/Policies', href: '/admin/quotes' },
      { label: 'Edit Quote', isActive: true }
    ];
  }
  
  if (pathname.startsWith('/admin/quotes/') && !pathname.endsWith('/edit') && !pathname.endsWith('/create')) {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Quotes/Policies', href: '/admin/quotes' },
      { label: 'Quote Details', isActive: true }
    ];
  }
  
  // Payment Management routes
  if (pathname === '/admin/payments') {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Payment Management', isActive: true }
    ];
  }
  
  // System Administration routes
  if (pathname === '/admin/database') {
    return [
      { label: topNav?.label || 'System Administration', href: topNav?.href },
      { label: 'Database Management', isActive: true }
    ];
  }
  
  // Notification Management routes
  if (pathname === '/admin/notifications') {
    return [
      { label: topNav?.label || 'Clients and Policies', href: topNav?.href },
      { label: 'Notifications', isActive: true }
    ];
  }
  
  if (pathname === '/staticHealthDashboard.html') {
    return [
      { label: topNav?.label || 'System Administration', href: topNav?.href },
      { label: 'Health Check', isActive: true }
    ];
  }
  
  // Document Templates
  if (pathname === '/admin/document-templates') {
    return [
      { label: topNav?.label || 'Products & Templates', href: topNav?.href },
      { label: 'Templates', isActive: true }
    ];
  }
  
  // Coverage Management
  if (pathname === '/admin/coverage') {
    return [
      { label: topNav?.label || 'Products & Templates', href: topNav?.href },
      { label: 'Coverage Management', isActive: true }
    ];
  }
  
  // Reports
  if (pathname === '/reports') {
    return [
      { label: topNav?.label || 'Reports, Accounting & Finance', href: topNav?.href },
      { label: 'Reports', isActive: true }
    ];
  }
  
  // Default: use top nav if available
  if (topNav && topNav.id !== 'dashboards') {
    return [
      { label: topNav.label, href: topNav.href, isActive: true }
    ];
  }
  
  return [{ label: 'Dashboards', href: '/dashboard' }];
}

